/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import isEmpty from 'lodash.isempty';
import isUndefined from 'lodash.isundefined';
import get from 'lodash/get';
import has from 'lodash/has';
import {translate} from '@ion/core/src/locale/locale';
import {
  BulkSettingFieldValues,
  GenericObject,
  SCIPendingSettings
} from '@ion/core/src/types/entities';
import {BulkSCIStyleProps} from './BulkSCISettings';
import {SCIAdvanceControlsMenu} from './SCIAdvanceControlsMenu';
import {SCIAdvanceSensorMenu} from './SCIAdvanceSensorMenu';
import {SCIAdvanceTagMenu} from './SCIAdvanceTagMenu';
import {
  availableModels,
  getActiveTabIndex,
  pendingValueCheck
} from './SCISettingsUtils';

export interface SCIAdvanceTabProps extends BulkSCIStyleProps {
  deviceModel: string;
  advanceTabFields: GenericObject[];
  classes: {
    [key: string]: string;
  };
  handleCustomSwitchChange: Function;
  handleAdvanceFieldChange: Function;
  noUpdate: boolean;
  sciPendingSettingData: SCIPendingSettings;
  isTagDetectionOn: boolean;
  initialAdvance: boolean;
  handleTagDetectionMode: Function;
  bulkSCI?: boolean;
}

export interface GetOptions {
  value: string | boolean | any;
  label: string | any;
};

export type GetOptionsResult = GetOptions[];

const dropdownStyle = {
  width : '80%'
};

/**
 * View or Edit Controls related information of an SCI device
 */
export const SCIAdvanceTab = ({
  deviceModel,
  advanceTabFields,
  classes,
  handleCustomSwitchChange,
  handleAdvanceFieldChange,
  noUpdate,
  sciPendingSettingData,
  isTagDetectionOn,
  initialAdvance,
  handleTagDetectionMode,
  bulkSCI = false,
  isBulkAction = false
}: SCIAdvanceTabProps) => {
  const [advancedActiveTab, setAdvancedActiveTab] = useState<string>('controls');
  function handleAdvanceTabsChange(event, newValue) {
    setAdvancedActiveTab(newValue);
  };

  const getOptions = (key: string): GetOptionsResult => {
    const entries = translate(`admin-devices:view.sciSettings.${key}`, {
      returnObjects : true
    });
    const result = Object.entries(entries).map(([value, label]) => (
      {value, label}
    ));
    return result;
  };

  const advanceDelayOptions = useMemo((): GetOptionsResult => (
    getOptions('advanceDelayOptions')
  ), []);

  const advanceTagOptions = useMemo((): GetOptionsResult => {
    return getOptions('advanceTagOptions');
  }, []);

  const reportAndLogOptions = useMemo((): GetOptionsResult => (
    getOptions('reportOptions')
  ), []);

  const isVehicleInput = useCallback((key): boolean => {
    return (key !== 'minVehicleInput1' && key !== 'maxVehicleInput1');
  }, []);

  const isTagDetectionMode = useCallback((key, value): boolean => {
    return (key !== 'label' || value === translate('admin-devices:view.sciSettings.tagDetectionMode'));
  }, []);

  const isAdvanceEmpty = useCallback((activeFieldsData): boolean => {
    const res = activeFieldsData.filter((each) => !isEmpty(each));
    return !!res.length;
  }, [advanceTabFields]);

  const fields = useMemo(() => (
    `${BulkSettingFieldValues.on} ${isBulkAction ? BulkSettingFieldValues.bulkDefault : BulkSettingFieldValues.default} ${BulkSettingFieldValues.off}`
  ), []);

  const advanceTabUI = (activeFieldsData, activeTab) => {
    return (
      <div>
        {(isAdvanceEmpty(activeFieldsData) && activeTab !== 1) && (
          <div className={isBulkAction ? classes.bulkAdvancedRadioLabel : classes.advancedRadioLabel}>
            {fields}
          </div>
        )}
        {(activeFieldsData?.length && isAdvanceEmpty(activeFieldsData)) ? activeFieldsData.map((field, {label}) => {
          return (
            <div className={classes.flexStyle}>
              {Object.entries(field).map(([key, value]) => {
                const menuItem = key;
                if (isTagDetectionMode(key, value)) {
                  return (<div className={isBulkAction ? classes.bulkAdvanceTabPaper : classes.advanceTabPaper}>
                    {(isVehicleInput(key)) && (
                      <div style = {dropdownStyle}>
                        {(activeTab !== 1 && activeTab !== 2) && (
                          <SCIAdvanceControlsMenu bulkSCI={bulkSCI}
                            field={field}
                            classes={classes}
                            noUpdate={noUpdate}
                            menu={menuItem}
                            value={value}
                            activeTab={activeTab}
                            handleAdvanceFieldChange={handleAdvanceFieldChange}
                            advanceDelayOptions={advanceDelayOptions}
                            reportAndLogOptions={reportAndLogOptions}
                            initialAdvance={initialAdvance}
                            sciPendingSettingData={sciPendingSettingData}/>
                        )}
                        {activeTab === 1 && (
                          <SCIAdvanceTagMenu field={field}
                            classes={classes}
                            noUpdate={noUpdate}
                            menu={menuItem}
                            value={value}
                            activeTab={activeTab}
                            handleAdvanceFieldChange={handleAdvanceFieldChange}
                            advanceTagOptions={advanceTagOptions}
                            initialAdvance={initialAdvance}
                            sciPendingSettingData={sciPendingSettingData}
                            handleTagDetectionMode={handleTagDetectionMode}/>
                        )}
                        {activeTab === 2 && (
                          <SCIAdvanceSensorMenu bulkSCI={bulkSCI}
                            field={field}
                            classes={classes}
                            noUpdate={noUpdate}
                            menu={menuItem}
                            value={value}
                            handleCustomSwitchChange={handleCustomSwitchChange}/>
                        )}
                      </div>
                    )}
                    {!isEmpty(sciPendingSettingData) && has(sciPendingSettingData, key) && (
                      <span className={classes.pendingStatus}>
                        {!isUndefined((get(sciPendingSettingData, key))) && (
                          <span>{`(Pending ${pendingValueCheck(get(sciPendingSettingData, key), key)})`}</span>
                        )}
                      </span>
                    )}
                  </div>
                  );
                }
              })}
            </div>
          );
        }) : <div className={classes.noDataStyle}>{translate('admin-devices:view.sciSettings.noRowsMessage')}</div>}
      </div>
    );
  };
  const activeTabIndex = getActiveTabIndex(advancedActiveTab);
  return (
    <div>
      <Box sx={{borderBottom: 1, borderColor: 'divider', width: isBulkAction ? '435px' : 'auto'}}>
        <Tabs value={advancedActiveTab}
          onChange={handleAdvanceTabsChange}
          variant={isBulkAction ? 'scrollable' : 'standard'}
          scrollButtons={isBulkAction ? 'on' : 'off'}>
          <Tab className={classes.tabStyle} label={translate('admin-devices:view.sciSettings.controls')} value="controls"/>
          {(isTagDetectionOn || deviceModel.includes(availableModels[2].name) || deviceModel.includes(availableModels[6].name) || deviceModel.includes(availableModels[7].name)) && <Tab className={classes.tabStyle} label={translate('admin-devices:view.sciSettings.tagDetection')} value="tagDetection"/>}
          <Tab className={classes.tabStyle} label={translate('admin-devices:view.sciSettings.sensors')} value="sensors"/>
          <Tab className={classes.tabStyle} label={translate('admin-devices:view.sciSettings.stationary')} value="stationary"/>
          <Tab className={classes.tabStyle} label={translate('admin-devices:view.sciSettings.moving')} value="moving"/>
          <Tab className={classes.tabStyle} label={translate('admin-devices:view.sciSettings.ignition')} value="ignition"/>
        </Tabs>
      </Box>
      {advanceTabUI(advanceTabFields[activeTabIndex], activeTabIndex)}
    </div>
  );
};

export default SCIAdvanceTab;
